import React, { useState, useEffect } from 'react';
import { 
  Shield, CheckCircle, XCircle, AlertTriangle, RefreshCw, FileText,
  Scale, Lock, Clock, Users, ChevronRight, Eye, Check, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useCompany } from '../context/CompanyContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccountingControls = () => {
  const { companyId } = useCompany();
  const [loading, setLoading] = useState(true);
  const [equationCheck, setEquationCheck] = useState(null);
  const [trialBalance, setTrialBalance] = useState(null);
  const [diagnosis, setDiagnosis] = useState(null);
  const [pendingCorrections, setPendingCorrections] = useState([]);
  const [auditTrail, setAuditTrail] = useState([]);
  const [controlAccounts, setControlAccounts] = useState({});
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchAllData();
  }, [companyId]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const params = companyId ? `?company_id=${companyId}` : '';
      const [eqRes, tbRes, diagRes, pendRes, auditRes, ctrlRes] = await Promise.all([
        fetch(`${API_URL}/api/accounting-controls/verify-equation${params}`),
        fetch(`${API_URL}/api/accounting-controls/trial-balance${params}`),
        fetch(`${API_URL}/api/accounting-controls/diagnose-imbalance`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ company_id: companyId }) }),
        fetch(`${API_URL}/api/accounting-controls/pending-corrections${params}`),
        fetch(`${API_URL}/api/accounting-controls/audit-trail?limit=20`),
        fetch(`${API_URL}/api/accounting-controls/control-accounts`)
      ]);
      
      if (eqRes.ok) setEquationCheck(await eqRes.json());
      if (tbRes.ok) setTrialBalance(await tbRes.json());
      if (diagRes.ok) setDiagnosis(await diagRes.json());
      if (pendRes.ok) setPendingCorrections(await pendRes.json());
      if (auditRes.ok) setAuditTrail(await auditRes.json());
      if (ctrlRes.ok) setControlAccounts(await ctrlRes.json());
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const approveCorrection = async (draftId) => {
    try {
      const res = await fetch(`${API_URL}/api/accounting-controls/approve-correction/${draftId}?approver_id=admin`, { method: 'POST' });
      if (res.ok) {
        alert('Correction approved and posted');
        fetchAllData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Shield },
    { id: 'trial-balance', label: 'Trial Balance', icon: Scale },
    { id: 'corrections', label: 'Pending Corrections', icon: AlertTriangle },
    { id: 'controls', label: 'Control Accounts', icon: Lock },
    { id: 'audit', label: 'Audit Trail', icon: Clock }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-sky-600" />
            Accounting Controls
          </h1>
          <p className="text-gray-500 mt-1">Monitor and enforce accounting integrity</p>
        </div>
        <Button onClick={fetchAllData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
              activeTab === tab.id ? 'bg-sky-100 text-sky-700 border-b-2 border-sky-600' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-sky-600 animate-spin" />
        </div>
      ) : (
        <>
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-3 gap-6">
              {/* Accounting Equation */}
              <Card className={equationCheck?.is_balanced ? 'border-emerald-200' : 'border-red-200'}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    {equationCheck?.is_balanced ? <CheckCircle className="w-5 h-5 text-emerald-600" /> : <XCircle className="w-5 h-5 text-red-600" />}
                    Accounting Equation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className={`text-lg font-bold ${equationCheck?.is_balanced ? 'text-emerald-600' : 'text-red-600'}`}>
                    {equationCheck?.is_balanced ? 'BALANCED' : 'IMBALANCED'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Assets = Liabilities + Equity</p>
                  <div className="mt-3 space-y-1 text-sm">
                    <div className="flex justify-between"><span>Assets:</span><span className="font-mono">${equationCheck?.assets?.toLocaleString() || 0}</span></div>
                    <div className="flex justify-between"><span>Liabilities:</span><span className="font-mono">${equationCheck?.liabilities?.toLocaleString() || 0}</span></div>
                    <div className="flex justify-between"><span>Equity:</span><span className="font-mono">${equationCheck?.equity?.toLocaleString() || 0}</span></div>
                    <div className="flex justify-between"><span>Retained Earnings:</span><span className="font-mono">${equationCheck?.retained_earnings?.toLocaleString() || 0}</span></div>
                  </div>
                  {!equationCheck?.is_balanced && (
                    <p className="mt-2 text-sm text-red-600">Difference: ${equationCheck?.difference?.toLocaleString()}</p>
                  )}
                </CardContent>
              </Card>

              {/* Trial Balance */}
              <Card className={trialBalance?.is_balanced ? 'border-emerald-200' : 'border-red-200'}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    {trialBalance?.is_balanced ? <CheckCircle className="w-5 h-5 text-emerald-600" /> : <XCircle className="w-5 h-5 text-red-600" />}
                    Trial Balance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className={`text-lg font-bold ${trialBalance?.is_balanced ? 'text-emerald-600' : 'text-red-600'}`}>
                    {trialBalance?.is_balanced ? 'BALANCED' : 'IMBALANCED'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Total Debits = Total Credits</p>
                  <div className="mt-3 space-y-1 text-sm">
                    <div className="flex justify-between"><span>Total Debits:</span><span className="font-mono">${trialBalance?.total_debits?.toLocaleString() || 0}</span></div>
                    <div className="flex justify-between"><span>Total Credits:</span><span className="font-mono">${trialBalance?.total_credits?.toLocaleString() || 0}</span></div>
                  </div>
                  {!trialBalance?.is_balanced && (
                    <p className="mt-2 text-sm text-red-600">Difference: ${trialBalance?.difference?.toLocaleString()}</p>
                  )}
                </CardContent>
              </Card>

              {/* Diagnosis Summary */}
              <Card className={diagnosis?.has_issues ? 'border-amber-200' : 'border-emerald-200'}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    {diagnosis?.has_issues ? <AlertTriangle className="w-5 h-5 text-amber-600" /> : <CheckCircle className="w-5 h-5 text-emerald-600" />}
                    System Diagnosis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className={`text-lg font-bold ${diagnosis?.has_issues ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {diagnosis?.has_issues ? `${diagnosis?.issue_count} ISSUES` : 'NO ISSUES'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">AI-powered integrity check</p>
                  {diagnosis?.issues?.slice(0, 3).map((issue, i) => (
                    <div key={i} className="mt-2 p-2 bg-amber-50 rounded text-xs text-amber-800">
                      <span className={`px-1 py-0.5 rounded text-xs mr-1 ${issue.severity === 'critical' ? 'bg-red-200' : 'bg-amber-200'}`}>
                        {issue.severity}
                      </span>
                      {issue.description}
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Trial Balance Tab */}
          {activeTab === 'trial-balance' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Trial Balance</span>
                  <span className={`text-sm px-3 py-1 rounded ${trialBalance?.is_balanced ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                    {trialBalance?.is_balanced ? 'Balanced' : 'Imbalanced'}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="p-2 text-left">Account #</th>
                      <th className="p-2 text-left">Account Name</th>
                      <th className="p-2 text-left">Category</th>
                      <th className="p-2 text-right">Debit</th>
                      <th className="p-2 text-right">Credit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {trialBalance?.accounts?.map((acc, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="p-2 font-mono">{acc.account_number}</td>
                        <td className="p-2">{acc.account_name}</td>
                        <td className="p-2 text-gray-500">{acc.category}</td>
                        <td className="p-2 text-right font-mono">{acc.debit > 0 ? `$${acc.debit.toLocaleString()}` : '-'}</td>
                        <td className="p-2 text-right font-mono">{acc.credit > 0 ? `$${acc.credit.toLocaleString()}` : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-100 font-bold">
                    <tr>
                      <td colSpan="3" className="p-2">TOTALS</td>
                      <td className="p-2 text-right font-mono">${trialBalance?.total_debits?.toLocaleString() || 0}</td>
                      <td className="p-2 text-right font-mono">${trialBalance?.total_credits?.toLocaleString() || 0}</td>
                    </tr>
                  </tfoot>
                </table>
              </CardContent>
            </Card>
          )}

          {/* Pending Corrections Tab */}
          {activeTab === 'corrections' && (
            <Card>
              <CardHeader>
                <CardTitle>Pending Correction Drafts</CardTitle>
              </CardHeader>
              <CardContent>
                {pendingCorrections.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 text-emerald-400" />
                    <p>No pending corrections</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingCorrections.map((draft, i) => (
                      <div key={i} className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-mono text-sm">{draft.draft_id}</span>
                          <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded">{draft.status}</span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{draft.memo}</p>
                        <p className="text-xs text-gray-500 mb-3">{draft.ai_reasoning}</p>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => approveCorrection(draft.draft_id)}>
                            <Check className="w-4 h-4 mr-1" />Approve & Post
                          </Button>
                          <Button size="sm" variant="outline">
                            <X className="w-4 h-4 mr-1" />Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Control Accounts Tab */}
          {activeTab === 'controls' && (
            <Card>
              <CardHeader>
                <CardTitle>Control Account Restrictions</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500 mb-4">These accounts can only be posted to by their designated modules:</p>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(controlAccounts).map(([acctNum, info]) => (
                    <div key={acctNum} className="p-4 bg-slate-50 rounded-lg flex items-center gap-3">
                      <Lock className="w-5 h-5 text-amber-600" />
                      <div>
                        <p className="font-medium">{info.name}</p>
                        <p className="text-sm text-gray-500">Account: {acctNum}</p>
                        <p className="text-xs text-sky-600">Module: {info.module.toUpperCase()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Audit Trail Tab */}
          {activeTab === 'audit' && (
            <Card>
              <CardHeader>
                <CardTitle>Audit Trail</CardTitle>
              </CardHeader>
              <CardContent>
                {auditTrail.length === 0 ? (
                  <p className="text-center py-8 text-gray-500">No audit entries yet</p>
                ) : (
                  <div className="space-y-2">
                    {auditTrail.map((entry, i) => (
                      <div key={i} className="p-3 bg-slate-50 rounded-lg flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileText className="w-4 h-4 text-gray-400" />
                          <div>
                            <p className="font-medium text-sm">{entry.entry_id}</p>
                            <p className="text-xs text-gray-500">{entry.action} by {entry.user_id}</p>
                          </div>
                        </div>
                        <span className="text-xs text-gray-400">{new Date(entry.timestamp).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default AccountingControls;
